import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CandidatePreview from './components/CandidatePreview';
import PricingSection from './components/PricingSection';
import PaymentMethodSelector from './components/PaymentMethodSelector';
import PaymentForm from './components/PaymentForm';
import SecurityNotice from './components/SecurityNotice';
import ConfirmationSection from './components/ConfirmationSection';
import ProcessingIndicator from './components/ProcessingIndicator';

const ProfileUnlockPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [formData, setFormData] = useState({
    cardholderName: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    email: ''
  });
  const [errors, setErrors] = useState({});

  // Mock candidate data - in real app this would come from route state or API
  const candidateData = location?.state?.candidate || {
    id: "ANON-2024-1847",
    title: "Senior Full-Stack Developer",
    location: "Oslo, Norway",
    experience: "7+ years",
    availability: "Available now",
    matchPercentage: 94,
    skills: [
    "React", "Node.js", "TypeScript", "Python", "AWS",
    "Docker", "PostgreSQL", "GraphQL", "Next.js", "MongoDB"]

  };

  const referenceNumber = `WF-${Date.now()?.toString()?.slice(-8)}`;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (paymentMethod === 'card') {
      if (!formData?.cardholderName?.trim()) {
        newErrors.cardholderName = 'Cardholder name is required';
      }
      if (!formData?.cardNumber?.trim()) {
        newErrors.cardNumber = 'Card number is required';
      } else if (formData?.cardNumber?.replace(/\s/g, '')?.length < 16) {
        newErrors.cardNumber = 'Please enter a valid card number';
      }
      if (!formData?.expiryDate?.trim()) {
        newErrors.expiryDate = 'Expiry date is required';
      } else if (!/^\d{2}\/\d{2}$/?.test(formData?.expiryDate)) {
        newErrors.expiryDate = 'Please enter date as MM/YY';
      }
      if (!formData?.cvv?.trim()) {
        newErrors.cvv = 'CVV is required';
      } else if (formData?.cvv?.length < 3) {
        newErrors.cvv = 'Please enter a valid CVV';
      }
      if (!formData?.email?.trim()) {
        newErrors.email = 'Email address is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handlePayment = async () => {
    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);
    setIsError(false);

    try {
      // Simulate payment processing
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Simulate random success/failure for demo
      const isSuccessful = Math.random() > 0.2; // 80% success rate

      if (isSuccessful) {
        setIsSuccess(true);
        setTimeout(() => {
          navigate('/employer-search-interface', {
            state: {
              unlockedCandidate: {
                ...candidateData,
                name: "Erik Andersen",
                email: "erik.andersen@email.no",
                phone: "+47 123 45 678",
                photo: "https://images.unsplash.com/photo-1623184663110-89ba5b565eb6",
                photoAlt: "Professional headshot of Norwegian man with brown hair in dark blue shirt smiling at camera",
                isUnlocked: true
              }
            }
          });
        }, 2000);
      } else {
        setIsError(true);
        setTimeout(() => {
          setIsError(false);
          setIsProcessing(false);
        }, 3000);
      }
    } catch (error) {
      setIsError(true);
      setTimeout(() => {
        setIsError(false);
        setIsProcessing(false);
      }, 3000);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      navigate('/employer-search-interface');
    }
  };

  const handleRetry = () => {
    setIsError(false);
    setIsProcessing(false);
  };

  return (
    <>
      <div className="fixed inset-0 z-1000 flex items-center justify-center p-4 bg-background/90 backdrop-blur-md">
        <div
          className="absolute inset-0"
          onClick={handleClose} />

        
        <div className="relative w-full max-w-4xl bg-card rounded-2xl shadow-elevation-lg animate-fade-in max-h-[95vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-card border-b border-border rounded-t-2xl z-10">
            <div className="flex items-center justify-between p-6">
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Unlock Candidate Profile</h1>
                <p className="text-muted-foreground mt-1">
                  Get full access to candidate contact information
                </p>
              </div>
              <button
                onClick={handleClose}
                disabled={isProcessing}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-micro disabled:opacity-50"
                aria-label="Close payment modal">

                <Icon name="X" size={24} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Left Column - Candidate & Pricing */}
              <div>
                <CandidatePreview candidate={candidateData} />
                <PricingSection />
                <SecurityNotice />
              </div>

              {/* Right Column - Payment Form */}
              <div>
                <PaymentMethodSelector
                  selectedMethod={paymentMethod}
                  onMethodChange={setPaymentMethod} />

                
                <PaymentForm
                  paymentMethod={paymentMethod}
                  formData={formData}
                  onInputChange={handleInputChange}
                  errors={errors} />

                
                <ConfirmationSection
                  candidate={candidateData}
                  referenceNumber={referenceNumber} />

                
                {/* Action Buttons */}
                <div className="flex items-center justify-between pt-6 border-t border-border">
                  <Button
                    variant="ghost"
                    onClick={handleClose}
                    disabled={isProcessing}>

                    Cancel
                  </Button>
                  
                  <Button
                    onClick={handlePayment}
                    disabled={isProcessing}
                    loading={isProcessing}
                    iconName={isProcessing ? undefined : "CreditCard"}
                    iconPosition="left"
                    size="lg">

                    {isProcessing ? 'Processing...' : 'Pay 149 kr'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Processing/Success/Error Overlay */}
      <ProcessingIndicator
        isProcessing={isProcessing}
        isSuccess={isSuccess}
        isError={isError} />


      {/* Error Retry Modal */}
      {isError &&
      <div className="fixed inset-0 z-1003 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl p-6 shadow-elevation-lg max-w-md w-full mx-4 text-center">
            <div className="h-12 w-12 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertCircle" size={24} className="text-destructive" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Payment Failed</h3>
            <p className="text-muted-foreground text-sm mb-6">
              We couldn't process your payment. Please check your payment details and try again.
            </p>
            <div className="flex space-x-3">
              <Button variant="ghost" onClick={handleClose} fullWidth>
                Cancel
              </Button>
              <Button onClick={handleRetry} fullWidth>
                Try Again
              </Button>
            </div>
          </div>
        </div>
      }
    </>);

};

export default ProfileUnlockPayment;